import {
  array,
  bool,
  func,
  number,
  object,
  oneOf,
  oneOfType,
  string,
} from 'prop-types'
import { useEffect } from 'react'
import VirtualList from 'react-tiny-virtual-list-oss'
import { Spacing } from '@opallabs/gem'
import { testFeature } from '@opallabs/principles'
import { kuiperClassNames } from '../../utils'
import OptionToken from '../OptionToken'
import * as styles from './styles'

const isHighlighted = (option, highlightedOption) => {
  if (!highlightedOption) return false
  return option.id && option.id === highlightedOption.id
}

const getChanges = (newSelections, oldSelections, selectedComparator) =>
  newSelections.reduce(
    (accum, option) => {
      const selected = selectedComparator(option, oldSelections)
      if (selected) {
        return {
          ...accum,
          toRemove: accum.toRemove.concat(option),
        }
      }
      return {
        ...accum,
        toSelect: accum.toSelect.concat(option),
      }
    },
    {
      toSelect: [],
      toRemove: [],
    }
  )

const MenuContents = ({
  avatared,
  disableKuiperStyles,
  highlightedIndex,
  highlightedOption,
  isMultiSelect,
  noOptionsText,
  noOptionsElement,
  onChange,
  onHoverOption,
  onRemove,
  onSelect,
  optionDecorator,
  options,
  optionsType,
  role,
  rowHeight,
  selectedComparator,
  selectedOptions,
  showDeselectIcon,
  theme,
}) => {
  const isAdmin = window.OpalMoments && window.OpalMoments.State.is_brand_admin
  const allowInvitations =
    (isAdmin || testFeature('users_can_invite')) && window.openInviter

  useEffect(() => {
    if (onChange) onChange()
  }, [onChange, selectedOptions])

  if (!options.length) {
    if (optionsType && optionsType === 'users' && allowInvitations) {
      const no_results_string =
        typeof window.Opal !== 'undefined' && window.Opal.State.brands_enabled
          ? 'Opal or this workspace'
          : 'Opal'

      return (
        /* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
        <div
          className={kuiperClassNames('empty', 'no-users')}
          aria-live="polite"
        >
          This person doesn&apos;t appear to be in {no_results_string}.
          <br />
          <a onClick={() => window.openInviter('user_menu')}>
            Invite them to collaborate with you!
          </a>
        </div>
        /* eslint-enable jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
      )
    }
    return (
      <div className={kuiperClassNames('empty')} aria-live="polite">
        {noOptionsElement || noOptionsText}
      </div>
    )
  }

  const onChooseOption = (...newSelections) => {
    if (!isMultiSelect) {
      const option = newSelections[0]
      const shouldRemove = selectedComparator(option, selectedOptions)
      if (option) {
        if (shouldRemove && onRemove) {
          onRemove(option)
        } else {
          onSelect(option)
        }

        if (onChange) onChange(option)
      }
      return
    }

    const { toRemove, toSelect } = getChanges(
      newSelections,
      selectedOptions,
      selectedComparator
    )

    if (onSelect && toSelect.length) {
      onSelect(...toSelect)
    }
    if (onRemove && toRemove.length) {
      onRemove(...toRemove)
    }
    if (onChange && (toSelect.length || toRemove.length)) {
      onChange(...toSelect.concat(toRemove))
    }
  }

  const numItems = (options && options.length) || 1
  const computeItemSize = (index) => {
    if (!disableKuiperStyles) return rowHeight
    if (options.length === 0) return rowHeight

    const spacingSize = index + 1 === options.length ? 0 : Spacing.XS
    return rowHeight + spacingSize
  }
  const totalItemSize = options.reduce(
    (memo, _, index) => memo + computeItemSize(index),
    0
  )
  const listHeight = Math.min(totalItemSize, 300)

  const optionRole = role === 'listbox' ? 'option' : 'menuitem'

  return (
    <div
      css={
        disableKuiperStyles ? styles.container({ height: listHeight }) : null
      }
      className={kuiperClassNames(
        disableKuiperStyles || 'menu-container',
        `size-n${numItems}`
      )}
    >
      <VirtualList
        width="100%"
        height={listHeight}
        scrollToIndex={highlightedIndex}
        scrollToAlignment="auto"
        estimatedItemSize={computeItemSize(0)}
        itemCount={options.length}
        overscanCount={10}
        itemSize={computeItemSize}
        renderItem={({ index, style }) => {
          let option = options[index]
          if (optionDecorator) {
            option = optionDecorator(option)
          }
          const key = 'id' in option ? option.id : JSON.stringify(option)

          return (
            <div
              css={disableKuiperStyles ? styles.option : null}
              key={key}
              style={disableKuiperStyles ? style : {}}
            >
              <OptionToken
                avatared={avatared}
                disableKuiperStyles={disableKuiperStyles}
                isHighlighted={isHighlighted(option, highlightedOption)}
                isSelected={selectedComparator(option, selectedOptions)}
                onSelect={onChooseOption}
                onMouseEnter={() => onHoverOption()}
                option={option}
                role={optionRole}
                showDeselectIcon={showDeselectIcon}
                style={disableKuiperStyles ? {} : style}
                theme={theme}
                tooltip={option.tooltip || false}
              />
            </div>
          )
        }}
      />
    </div>
  )
}
MenuContents.displayName = 'Kuiper: MenuContents ← Menu'

MenuContents.defaultProps = {
  disableKuiperStyles: false,
  theme: 'light',
}

MenuContents.propTypes = {
  avatared: bool,
  disableKuiperStyles: bool,
  highlightedIndex: number,
  highlightedOption: object,
  isMultiSelect: bool,
  noOptionsText: string,
  noOptionsElement: object,
  onChange: func,
  onHoverOption: func,
  onRemove: func,
  onSelect: func,
  optionDecorator: func,
  options: array,
  optionsType: oneOf(['users']),
  role: oneOf(['listbox', 'menu']),
  rowHeight: number,
  selectedComparator: func,
  selectedOptions: oneOfType([array, object]),
  showDeselectIcon: bool,
  theme: oneOf(['dark', 'light']),
}

export default MenuContents
